import { defineStore } from 'pinia'
import axios from 'axios'
import { saveRecipeToDetailCache } from '@/utils/recipeCacheUtils'

const RANGE_CACHE_TTL_MS = 10 * 60 * 1000
const PERSIST_ITEM_DAY_RADIUS = 84

function formatIsoDate (date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export const usePlannerStore = defineStore('planner', {
  state: () => ({
    items: [],
    loading: false,
    error: null,
    _loadingDepth: 0,
    mealPlanStatus: {
      status: null,
      plan_start_date: null,
      plan_end_date: null,
      /** True while a preference row is in `processing` (authoritative; avoids stale `process_status` in the client). */
      has_active_generation: false,
      loading: false
    },
    planSeen: true,
    isPlanActive: false,
    planStartDate: null,
    planEndDate: null,
    planSummary: null,
    planPreferences: null,
    loadedRanges: {},
    lastMetaRefreshAt: null,
    isPlannerRouteActive: false,
    _rangeSeq: {},
    _fetchInFlight: {},
    _mealPlanStatusInFlight: null,
    _refreshMetaInFlight: null,
  }),
  actions: {
    setPlannerRouteActive (value) {
      this.isPlannerRouteActive = !!value
    },

    formatIsoDateLocal (date) {
      return formatIsoDate(date)
    },

    getWeekBounds (weekOffset = 0) {
      const today = new Date()
      const start = new Date(today)
      start.setDate(today.getDate() + weekOffset * 7)
      const end = new Date(start)
      end.setDate(start.getDate() + 6)

      return {
        start: formatIsoDate(start),
        end: formatIsoDate(end)
      }
    },

    _plannerRangeKey (startDate, endDate) {
      if (!startDate || !endDate) return 'full'
      return `${startDate}:${endDate}`
    },

    _requestKey (locale, startDate, endDate, fields, mode) {
      return JSON.stringify({
        locale: locale || '',
        start: startDate || '',
        end: endDate || '',
        fields: fields || '',
        mode: mode || ''
      })
    },

    _beginLoading () {
      this._loadingDepth += 1
      this.loading = true
    },

    _endLoading () {
      this._loadingDepth = Math.max(0, this._loadingDepth - 1)
      if (this._loadingDepth === 0) {
        this.loading = false
      }
    },

    isRangeLoaded (startDate, endDate) {
      if (!startDate || !endDate) return false
      if (Array.isArray(this.loadedRanges)) {
        this.loadedRanges = {}
        return false
      }
      const rangeKey = `${startDate}:${endDate}`
      const loadedAt = this.loadedRanges[rangeKey]
      if (!loadedAt) return false
      return Date.now() - loadedAt < RANGE_CACHE_TTL_MS
    },

    markRangeLoaded (startDate, endDate) {
      if (!startDate || !endDate) return
      const rangeKey = `${startDate}:${endDate}`
      this.loadedRanges[rangeKey] = Date.now()
    },

    invalidateRangeCache () {
      this.loadedRanges = {}
    },

    trimPersistedPlannerWindow () {
      const today = new Date()
      const min = new Date(today)
      min.setDate(today.getDate() - PERSIST_ITEM_DAY_RADIUS)
      const max = new Date(today)
      max.setDate(today.getDate() + PERSIST_ITEM_DAY_RADIUS)
      const minStr = formatIsoDate(min)
      const maxStr = formatIsoDate(max)

      this.items = (Array.isArray(this.items) ? this.items : []).filter((item) => {
        const d = String(item.date || '').slice(0, 10)
        return d >= minStr && d <= maxStr
      })

      if (Array.isArray(this.loadedRanges)) {
        this.loadedRanges = {}
        return
      }
      const next = {}
      for (const [k, t] of Object.entries(this.loadedRanges)) {
        const parts = k.split(':')
        if (parts.length !== 2) continue
        const [s, e] = parts
        if (e >= minStr && s <= maxStr) {
          next[k] = t
        }
      }
      this.loadedRanges = next
    },

    mergeItemsForRange (plannerItems, startDate, endDate) {
      if (!startDate || !endDate) {
        this.items = plannerItems
        this.trimPersistedPlannerWindow()
        return
      }

      const inRange = (date) => date >= startDate && date <= endDate
      const existingOutsideRange = (Array.isArray(this.items) ? this.items : [])
        .filter(item => !inRange(item.date))
      this.items = [...existingOutsideRange, ...plannerItems].sort((a, b) => {
        const dateDiff = String(a.date || '').localeCompare(String(b.date || ''))
        if (dateDiff !== 0) return dateDiff
        const timeDiff = String(a.time_suggestion || '').localeCompare(String(b.time_suggestion || ''))
        if (timeDiff !== 0) return timeDiff
        return Number(a.order || 0) - Number(b.order || 0)
      })
      this.trimPersistedPlannerWindow()
    },

    applyPlannerMetaFromPayload (payload) {
      if (!payload || typeof payload !== 'object') return
      if ('plan_seen' in payload) this.planSeen = payload.plan_seen ?? this.planSeen
      if ('is_plan_active' in payload) this.isPlanActive = payload.is_plan_active ?? this.isPlanActive
      if ('plan_start_date' in payload) this.planStartDate = payload.plan_start_date ?? this.planStartDate
      if ('plan_end_date' in payload) this.planEndDate = payload.plan_end_date ?? this.planEndDate
      if ('plan_summary' in payload) this.planSummary = payload.plan_summary ?? this.planSummary
      if ('preferences' in payload && payload.preferences) this.planPreferences = payload.preferences
    },

    async fetchWeekTriplet (locale, centerWeekOffset = 0, options = {}) {
      const { forceRefresh = false, lite = false, navigationDirection = 'none' } = options
      const centerBounds = this.getWeekBounds(centerWeekOffset)
      const adjacentOffsets = [centerWeekOffset - 1, centerWeekOffset + 1]
      const farOffset = navigationDirection === 'forward'
        ? centerWeekOffset + 2
        : navigationDirection === 'backward'
          ? centerWeekOffset - 2
          : null
      const prefetchOffsets = farOffset == null
        ? adjacentOffsets
        : [...new Set([...adjacentOffsets, farOffset])]

      this._beginLoading()
      try {
        // Always hydrate the visible week first, then prefetch neighbors.
        await this.fetchPlanner(locale, {
          startDate: centerBounds.start,
          endDate: centerBounds.end,
          skipIfCached: !forceRefresh,
          forceRefresh,
          mode: lite ? 'lite' : null,
          manageLoading: false
        })

        await Promise.allSettled(
          prefetchOffsets.map((offset) => this.fetchAdjacentWeek(locale, offset, { forceRefresh, lite, manageLoading: false }))
        )
      } finally {
        this._endLoading()
      }
    },

    async fetchAdjacentWeek (locale, weekOffset, options = {}) {
      const { forceRefresh = false, lite = false, manageLoading = false } = options
      const { start, end } = this.getWeekBounds(weekOffset)
      return this.fetchPlanner(locale, {
        startDate: start,
        endDate: end,
        skipIfCached: !forceRefresh,
        forceRefresh,
        mode: lite ? 'lite' : null,
        manageLoading
      })
    },

    /** Current week only, lite payload — for Recipes / RecipeDetails surfaces */
    async fetchPlannerCurrentWeekLite (locale, options = {}) {
      const { start, end } = this.getWeekBounds(0)
      return this.fetchPlanner(locale, {
        startDate: start,
        endDate: end,
        skipIfCached: options.skipIfCached !== false,
        forceRefresh: !!options.forceRefresh,
        mode: 'lite',
        manageLoading: options.manageLoading !== false
      })
    },

    resetPlannerSnapshot () {
      this.items = []
      this.error = null
      this.planSeen = true
      this.isPlanActive = false
      this.planStartDate = null
      this.planEndDate = null
      this.planSummary = null
      this.mealPlanStatus.status = null
      this.mealPlanStatus.plan_start_date = null
      this.mealPlanStatus.plan_end_date = null
      this.mealPlanStatus.has_active_generation = false
      this.invalidateRangeCache()
    },

    async refreshPlannerMeta (locale) {
      if (this._refreshMetaInFlight) {
        return this._refreshMetaInFlight
      }
      this._refreshMetaInFlight = (async () => {
        try {
          const response = await axios.get('/api/planner', {
            params: {
              locale,
              fields: 'meta'
            }
          })
          const payload = response.data || {}
          this.applyPlannerMetaFromPayload(payload)
          this.lastMetaRefreshAt = Date.now()
        } catch (err) {
          console.error('Error refreshing planner metadata:', err)
        } finally {
          this._refreshMetaInFlight = null
        }
      })()
      return this._refreshMetaInFlight
    },

    async fetchPlanner (locale, options = {}) {
      const {
      startDate = null,
      endDate = null,
      skipIfCached = false,
      forceRefresh = false,
      fields = null,
      mode = null,
      manageLoading = true,
      withPreferences = false,
    } = options

      if (!forceRefresh && skipIfCached && fields !== 'meta' && this.isRangeLoaded(startDate, endDate)) {
        return
      }

      const rangeKey = this._plannerRangeKey(startDate, endDate)
      const reqKey = this._requestKey(locale, startDate, endDate, fields, mode)
      const existing = this._fetchInFlight[reqKey]
      if (existing) {
        if (!forceRefresh) {
          return existing
        }
        // forceRefresh: discard the stale in-flight and start a fresh request.
        // _rangeSeq will increment below so the old response is ignored when it
        // eventually settles.
        delete this._fetchInFlight[reqKey]
      }

      if (!this._rangeSeq[rangeKey]) {
        this._rangeSeq[rangeKey] = 0
      }
      this._rangeSeq[rangeKey] += 1
      const mySeq = this._rangeSeq[rangeKey]

      let thisRun
      const run = (async () => {
        if (manageLoading) {
          this._beginLoading()
        }
        this.error = null

        try {
          const params = { locale }
          if (startDate && endDate) {
            params.start_date = startDate
            params.end_date = endDate
          }
          if (fields) params.fields = fields
          if (mode) params.mode = mode
          if (withPreferences) params.with_preferences = 1

          const response = await axios.get('/api/planner', { params })
          const payload = response.data || {}

          // Update mealPlanStatus BEFORE the range-staleness check.
          // Generation status is global (not range-specific), so it must survive
          // even if _rangeSeq discards the range data below.
          // Always merge generation flags from this response. Concurrent range
          // fetches carry the same user-level meta; last write wins is acceptable.
          if (!fields && ('process_status' in payload || 'has_active_generation' in payload)) {
            const nextStatus = payload.process_status ?? null
            const nextHasActive = typeof payload.has_active_generation === 'boolean'
              ? payload.has_active_generation
              : nextStatus === 'processing'
            this.mealPlanStatus = {
              ...this.mealPlanStatus,
              status: nextStatus,
              plan_start_date: payload.plan_start_date ?? this.mealPlanStatus.plan_start_date,
              plan_end_date: payload.plan_end_date ?? this.mealPlanStatus.plan_end_date,
              has_active_generation: nextHasActive,
            }
          }

          if (this._rangeSeq[rangeKey] !== mySeq) {
            return
          }

          if (fields === 'meta') {
            this.applyPlannerMetaFromPayload(payload)
            return
          }

          const plannerItems = Array.isArray(payload.data)
            ? payload.data
            : (Array.isArray(payload?.data?.data) ? payload.data.data : [])

          this.mergeItemsForRange(plannerItems, startDate, endDate)
          this.markRangeLoaded(startDate, endDate)
          this.applyPlannerMetaFromPayload(payload)

          if (Array.isArray(plannerItems)) {
            plannerItems.forEach(item => {
              if (item.recipe) {
                saveRecipeToDetailCache(item.recipe, locale)
              }
            })
          }
        } catch (err) {
          console.error('Error fetching planner:', err)
          this.error = err
          if (this._rangeSeq[rangeKey] === mySeq && (!startDate || !endDate)) {
            this.items = []
          }
        } finally {
          if (manageLoading) {
            this._endLoading()
          }
          // Only remove our own entry — a forceRefresh may have replaced it
          // with a newer promise before this finally block ran.
          if (this._fetchInFlight[reqKey] === thisRun) {
            delete this._fetchInFlight[reqKey]
          }
        }
      })()

      thisRun = run
      this._fetchInFlight[reqKey] = run
      return run
    },

    async assignRecipe (date, mealType, recipe, locale, order = null, timeSuggestion = null, fetchOptions = null) {
      this.error = null

      try {
        const payload = {
          date, meal_type: mealType, recipe_id: recipe.id
        }
        if (order !== null) payload.order = order
        if (timeSuggestion !== null) payload.time_suggestion = timeSuggestion

        await axios.post('/api/planner', payload)
        this.invalidateRangeCache()
        if (fetchOptions?.prefetchAdjacentWeeks) {
          await this.fetchWeekTriplet(locale, fetchOptions.weekOffset ?? 0, { forceRefresh: true, lite: false })
        } else if (fetchOptions && fetchOptions.startDate && fetchOptions.endDate) {
          await this.fetchPlanner(locale, fetchOptions)
        } else {
          await this.fetchWeekTriplet(locale, 0, { forceRefresh: true, lite: false })
        }
      } catch (err) {
        console.error('Error assigning recipe:', err)
        this.error = err
        throw err
      }
    },

    async removeItem (itemId) {
      this.error = null

      try {
        const deletedItem = this.items.find(item => item.id === itemId)
        await axios.delete(`/api/planner/${itemId}`)
        this.items = this.items.filter(item => item.id !== itemId)
        if (deletedItem?.date) {
          this.invalidateRangeCache()
        }
      } catch (err) {
        console.error('Error removing item:', err)
        this.error = err
        throw err
      }
    },

    async replaceWithAI (itemId, locale, notes = '') {
      this.error = null

      try {
        const payload = { locale }
        const trimmedNotes = (notes || '').trim()
        if (trimmedNotes) {
          payload.notes = trimmedNotes
        }
        const response = await axios.post(`/api/planner/${itemId}/replace-ai`, payload)
        const updatedItem = response.data.data

        if (updatedItem && updatedItem.recipe) {
          saveRecipeToDetailCache(updatedItem.recipe, locale)
        }

        const index = this.items.findIndex(item => item.id === itemId)
        if (index !== -1) {
          this.items[index] = updatedItem
          this.invalidateRangeCache()
        }

        return updatedItem
      } catch (err) {
        console.error('Error replacing item with AI:', err)
        this.error = err
        throw err
      }
    },

    /**
     * Apply planner changes streamed back by the AI coach so the week view
     * updates in place without a full refetch.
     */
    applyCoachChanges (changedItems = [], removedItemIds = [], locale = 'en') {
      for (const item of changedItems) {
        if (!item || item.id == null) continue
        if (item.recipe) {
          saveRecipeToDetailCache(item.recipe, locale)
        }
        const index = this.items.findIndex(existing => existing.id === item.id)
        if (index !== -1) {
          this.items[index] = item
        } else {
          this.items.push(item)
        }
      }

      if (removedItemIds.length) {
        this.items = this.items.filter(item => !removedItemIds.includes(item.id))
      }

      if (changedItems.length || removedItemIds.length) {
        this.invalidateRangeCache()
      }
    },

    /** @param {{ force?: boolean }} [options] — `force`: await any in-flight check, then GET again (planner mount). */
    async checkMealPlanStatus (options = {}) {
      const force = !!options.force
      if (this._mealPlanStatusInFlight) {
        if (!force) {
          return this._mealPlanStatusInFlight
        }
        try {
          await this._mealPlanStatusInFlight
        } catch {
          /* ignore */
        }
      }

      this._mealPlanStatusInFlight = (async () => {
        this.mealPlanStatus.loading = true
        try {
          const response = await axios.get('/api/user/meal-plan-status', {
            params: { _ts: Date.now() },
            headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' }
          })
          const root = response?.data || {}
          const nested = root.data && typeof root.data === 'object' && !Array.isArray(root.data) ? root.data : {}
          const payload = { ...root, ...nested }

          const nextStatus = payload.process_status ?? null
          const nextHasActive = typeof payload.has_active_generation === 'boolean'
            ? payload.has_active_generation
            : nextStatus === 'processing'

          this.mealPlanStatus = {
            ...this.mealPlanStatus,
            status: nextStatus,
            plan_start_date: payload.plan_start_date ?? null,
            plan_end_date: payload.plan_end_date ?? null,
            has_active_generation: nextHasActive
          }

          // Align celebration / banner fields with GET /api/planner (same keys from meal-plan-status).
          this.applyPlannerMetaFromPayload(payload)

          return this.mealPlanStatus.status
        } catch (err) {
          console.error('Error checking meal plan status:', err)
          return null
        } finally {
          this.mealPlanStatus.loading = false
          this._mealPlanStatusInFlight = null
        }
      })()

      return this._mealPlanStatusInFlight
    },

    setMealPlanStatus (status) {
      this.mealPlanStatus.status = status
      if (status === 'processing') {
        this.mealPlanStatus.has_active_generation = true
      } else if (status != null) {
        this.mealPlanStatus.has_active_generation = false
      }
    },

    async markPlanSeen () {
      try {
        await axios.post('/api/planner/mark-seen')
        this.planSeen = true
      } catch (err) {
        console.error('Error marking meal plan as seen:', err)
        throw err
      }
    },

  },
  getters: {
    groupedByDate: (state) => {
      const items = Array.isArray(state.items) ? state.items : []

      return items.reduce((acc, item) => {
        if (!acc[item.date]) acc[item.date] = []
        acc[item.date].push(item)
        return acc
      }, {})
    }
  },
  persist: {
    paths: ['items', 'mealPlanStatus', 'planSeen', 'isPlanActive', 'planStartDate', 'planEndDate', 'planSummary', 'planPreferences', 'loadedRanges', 'lastMetaRefreshAt'],
    afterRestore (ctx) {
      ctx.store.mealPlanStatus.loading = false
    },
  }
})
